import React, { useEffect, useMemo, useState } from "react";
// @ts-ignore router version update needed
import { useSearchParams } from "react-router-dom";
import { breakpoints, IconButton, useMediaQuery } from "@tvg/design-system";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import useScrollRaceIntoView from "../../../../hooks/useScrollRaceIntoView";
import useRaceSelectorScroll from "../../../../hooks/useRaceSelectorScroll";
import { FadeLayer, ListContainer, Navigation } from "./styled-components";
import ListItem from "../ListItem";

export interface ListProps {
  races: Array<RaceProgram>;
  isMobileView?: boolean;
}

const List = ({ races, isMobileView = false }: ListProps) => {
  const [searchParams] = useSearchParams();
  const selectedRaceNumber = searchParams.get("race");

  const numberOfRaces = races.length;
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);

  const {
    listOfRacesRef,
    isOverFlowing,
    disableScrollRight,
    handleScrollTo,
    scrollAmount,
    getWidthToScroll
  } = useRaceSelectorScroll(races[0]?.track?.trackCode, isMobile);

  const [refs] = useScrollRaceIntoView({
    races,
    selectedRaceNumber,
    isOverFlowing,
    isMobile,
    scrollableWrapperRef: listOfRacesRef
  });

  const { scrollWidth, clientWidth } = listOfRacesRef.current ?? {};

  const isAtTheEnd =
    (scrollWidth || 0) - (clientWidth || 0) === Math.trunc(scrollAmount);

  const [fadeLayerWidth, setFadeLayerWidth] = useState<number>(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((element) => {
      setFadeLayerWidth(element[0].contentRect.width);
    });

    if (listOfRacesRef.current) {
      resizeObserver.observe(listOfRacesRef.current as Element);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [listOfRacesRef.current]);

  // Render memorized list of races
  const renderList = useMemo(() => {
    let foundRaceOfficial: boolean = false;
    let raceIdToDivide: string | undefined;

    return (
      races &&
      races.map((race) => {
        if (race.status && race.status.code === RaceStatusEnum.RACE_OFFICIAL) {
          foundRaceOfficial = true;
        } else if (foundRaceOfficial && raceIdToDivide === undefined) {
          raceIdToDivide = race.id;
        }

        return (
          <ListItem
            key={race.id}
            ref={(element: HTMLLIElement) => {
              if (race.raceNumber) {
                refs.current[race.raceNumber] = element;
              }
            }}
            race={race}
            isActive={selectedRaceNumber === race.raceNumber}
            isFirstOpenRace={raceIdToDivide === race.id}
            isMobile={isMobileView}
          />
        );
      })
    );
  }, [races, selectedRaceNumber]);

  return (
    <>
      <ListContainer
        key={`raceList-${races[0]?.id}`}
        isOverFlowing={isOverFlowing}
        ref={listOfRacesRef}
        data-qa-label="race-list-container"
      >
        {renderList}
      </ListContainer>
      {isMobile && (
        <>
          {scrollAmount > 0 && (
            <FadeLayer
              data-qa-label="fade-layer-right"
              orientation="right"
              width={fadeLayerWidth}
            />
          )}
          {!isAtTheEnd && (
            <FadeLayer
              data-qa-label="fade-layer-left"
              orientation="left"
              width={fadeLayerWidth}
            />
          )}
        </>
      )}
      {isOverFlowing && (
        <>
          <FadeLayer
            data-qa-label="fade-layer"
            orientation={scrollAmount <= 0 ? "left" : "right"}
            width={fadeLayerWidth}
          />
          <Navigation>
            <IconButton
              qaLabel="left-swipe-button"
              variant="secondary_dark"
              size="m"
              iconName="arrowLeft"
              tertiaryColor="white.000"
              onPress={() => handleScrollTo(-getWidthToScroll(numberOfRaces))}
              isDisabled={scrollAmount <= 0}
              ml={12}
            />
            <IconButton
              qaLabel="right-swipe-button"
              variant="secondary_dark"
              size="m"
              iconName="arrowRight"
              tertiaryColor="white.000"
              onPress={() => handleScrollTo(getWidthToScroll(numberOfRaces))}
              ml={12}
              isDisabled={disableScrollRight}
            />
          </Navigation>
        </>
      )}
    </>
  );
};

export default List;
